import { useLocation, useNavigate, useParams } from "react-router-dom"
import useLocalStorageState from "use-local-storage-state"
import { PipelineEditor } from "./PipelineEditor"
import { toast } from "sonner"
import { usePostRequest, useGetRequest } from "../../Utils/hooks/QueryHandler"
import { useEffect, useState } from "react"
import { pipeline } from "zod"
import { useGetEssentials } from "../../Utils/hooks/useGetEssentials"

// Utility function for decoding pipeline IDs from URL parameters
const decodePipelineId = (encodedId) => {
    try {
        let base64 = encodedId.replace(/-/g, "+").replace(/_/g, "/")
        const padding = base64.length % 4
        if (padding) {
            base64 += "=".repeat(4 - padding)
        }
        const decodedStr = atob(base64)
        return Number.parseInt(decodedStr, 10)
    } catch (error) {
        return null
    }
}

export const DuplicatePipelinePage = () => {

    const { headers, extras, navigate, customerId, loginUserId, userType } = useGetEssentials()

    const location = useLocation()
    const data = location.state || null

    const { encodedId } = useParams()
    const [originalPipeline, setOriginalPipeline] = useState(null)
    const [originalStages, setOriginalStages] = useState([])
    const [isDataReady, setIsDataReady] = useState(false)

    // Decode pipeline ID from URL
    const pipelineId = decodePipelineId(encodedId)
    // Redirect if invalid pipeline ID
    useEffect(() => {
        if (!pipelineId) {
            toast.error("Invalid pipeline ID")
            navigate("/leads")
        }
    }, [pipelineId, navigate])

    // Fetch original pipeline data
    const {
        data: pipelineApiData,
        isLoading: isPipelineLoading,
        error: pipelineError,
        refetch: refetchPipeline
    } = useGetRequest(`lead/fetchPipelines/${customerId}/${data?.module_name}/${loginUserId}/${userType}`, "fetchPipelinesForDuplicate", headers, extras, {
        enabled: !!customerId && !!pipelineId,
    })

    // Fetch original pipeline stages
    const {
        data: pipelineStageData,
        isLoading: isPipelineStageLoading,
        error: stageError,
    } = useGetRequest(
        `lead/fetchPipelineStage/${pipelineId}/${customerId}`,
        "fetchPipelineStageForDuplicate",
        headers,
        extras,
        {
            enabled: !!customerId && !!pipelineId,
        },
    )

    // Create pipeline mutation hook
    const { mutate: createPipeline, isLoading: isCreating } = usePostRequest(
        "lead/createPipeline/",
        false,
        { headers },
        extras,
    )

    // Set original pipeline data when fetched
    useEffect(() => {
        if (pipelineApiData?.status === "success" && pipelineId) {
            const pipeline = pipelineApiData.data.find((p) => p.id == pipelineId)
            if (pipeline) {
                setOriginalPipeline(pipeline)
            } else {
                toast.error("Pipeline not found")
                navigate("/leads")
            }
        } else if (pipelineApiData?.status === "fail") {
            toast.error(pipelineApiData.message || "Failed to fetch pipeline data")
            navigate("/leads")
        }
    }, [pipelineApiData, pipelineId, navigate])

    // Set original stages data when fetched
    useEffect(() => {
        if (pipelineStageData?.status === "success") {
            const formattedStages = pipelineStageData.data.map((stage) => ({
                ...stage,
                sequence: String(stage.sequence),
                probability: Math.min(100, Math.max(0, stage.probability || 0)),
                rotting_days: stage.rotting_days === null ? null : stage.rotting_days,
                rotting_enabled: stage.rotting_days !== null && stage.rotting_days > 0,
            }))
            setOriginalStages(formattedStages)
        } else if (pipelineStageData?.status === "fail") {
            toast.error(pipelineStageData.message || "Failed to fetch pipeline stages")
            navigate("/leads")
        }
    }, [pipelineStageData, navigate])

    // Check if data is ready
    useEffect(() => {
        if (originalPipeline && originalStages.length > 0) {
            setIsDataReady(true)
        }
    }, [originalPipeline, originalStages])

    // Handle saving duplicated pipeline via API
    const handleSavePipeline = async (updatedData, hasActualChanges = true) => {
        try {
            // Validate required data
            if (!updatedData.pipeline_name?.trim()) {
                toast.error("Pipeline name is required")
                return
            }

            if (!updatedData.pipeline_stage || updatedData.pipeline_stage.length === 0) {
                toast.error("Pipeline must have at least one stage")
                return
            }

            const stagesPayload = updatedData.pipeline_stage
                .filter((stage) => stage.action !== "Delete") // Exclude deleted stages
                .map((stage, index) => ({
                    stage_name: stage.stage_name?.trim() || `Stage ${index + 1}`,
                    stage_id: "", // Always empty for new pipeline
                    sequence: String(index + 1), // Ensure proper sequence
                    rotting_days: stage.rotting_days || null,
                    probability: Math.min(100, Math.max(0, Number(stage.probability) || 0)),
                }))

            const payload = {
                customer_id: customerId,
                pipeline_name: updatedData.pipeline_name.trim(),
                user_login_id: loginUserId,
                pipeline_stage: stagesPayload,
            }

            createPipeline(payload, {
                onSuccess: async (response) => {

                    if (response?.status === "success") {
                        toast.success(response.message || "Pipeline duplicated successfully")
                        await refetchPipeline()
                        setTimeout(() => {
                            window.location.href = "/leads";
                        }, 500);
                    } else {
                        toast.error(response?.message || "Failed to duplicate pipeline")
                    }
                },
                onError: (error) => {
                    console.error("Create pipeline error:", error)
                    toast.error(error?.message || "An error occurred while duplicating pipeline")
                },
            })
        } catch (error) {
            console.error("Save pipeline error:", error)
            toast.error("An error occurred while saving pipeline")
        }
    }

    // Navigate back to leads page
    const handleCancel = () => {
        navigate("/leads")
    }

    // Handle temporary stage deletion (no API call needed for unsaved stages)
    const handleStageDeleteRequest = (stageId) => {
        // For duplicate pipeline, all stages are temporary, so no API call needed
        return Promise.resolve()
    }

    // Handle errors
    useEffect(() => {
        if (pipelineError) {
            console.error("Pipeline fetch error:", pipelineError)
            toast.error("Failed to load pipeline data")
            navigate("/leads")
        }
    }, [pipelineError, navigate])

    useEffect(() => {
        if (stageError) {
            console.error("Stage fetch error:", stageError)
            toast.error("Failed to load pipeline stages")
            navigate("/leads")
        }
    }, [stageError, navigate])

    // Show loading state while fetching data
    if (isPipelineLoading || isPipelineStageLoading || !isDataReady || isCreating) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{isCreating ? "Creating pipeline..." : "Loading..."}</span>
                </div>
            </div>
        )
    }

    // Generate duplicate pipeline name
    const duplicatePipelineName = `${originalPipeline.pipeline_name} Copy`

    return (
        <PipelineEditor
            pipelineName={duplicatePipelineName}
            pipelineStage={originalStages}
            onClose={handleCancel}
            onSave={handleSavePipeline}
            onDuplicate={() => { }} // Not applicable for duplicate
            onDeletePipeline={() => { }} // Not applicable for duplicate
            onStageDeleteRequest={handleStageDeleteRequest}
            pipelineCount={1} // Not relevant for duplicate
            isCreatingNewPipeline={true} // Treat as new pipeline creation
            isDuplicatingPipeline={true} // New flag to indicate this is duplication mode
            leadsData={[]} // No leads data for duplicated pipeline
            pipelineId={null} // No pipeline ID for duplicate (it's a new pipeline)
        />
    )
}
